import { Button, Empty, Input, Pagination, Select, Spin, Table, Tag } from 'antd';
import { EyeOutlined, FilterOutlined, ReloadOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { makeRequestStateless } from '@utils/helpers';
import { FilterComponent } from '@comps/components';
import { getRequestFilters } from '../../../modules/common/commonFilterFields';
import { getMyApprovalsFilterReq, getMyApprovalsReq, getMyRequestsReq } from './requests';
import { parseFilterString } from './myWorkFilters';
import './my-work-inbox.css';

const TYPE_OPTIONS = [
  'leave_request', 'relaxation_request', 'attendance_request', 'overtime_request',
  'expense_request', 'lend_request', 'official_duty_request', 'travel_request',
  'transfer_request', 'promotion_request', 'probation_request', 'requisition_request',
].map((value) => ({ value, label: title(value) }));

const MyWorkInbox = ({ approvals = false }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState({ data: [], meta: {} });
  const [filters, setFilters] = useState({ page: 1, search: '', status: undefined, type: undefined });
  const [advancedFilters, setAdvancedFilters] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    makeRequestStateless(
      approvals ? getMyApprovalsReq : getMyRequestsReq,
      { ...filters, ...parseFilterString(advancedFilters) },
      (response) => {
        setResult(response);
        setLoading(false);
      },
      () => setLoading(false)
    );
  }, [advancedFilters, approvals, filters]);

  useEffect(load, [load]);

  const openDetail = (record) => {
    if (!record.detail_route) return;
    navigate(`/${String(record.detail_route).replace(/^app\//, '').replace(/#$/, '')}`, {
      state: {
        approvalContext: {
          requestId: record.id,
          requestType: record.type,
          approvalApi: record.approval_api,
          delegatorId: record.delegator?.id || null,
        },
      },
    });
  };

  const columns = [
    {
      title: 'Type',
      dataIndex: 'type_label',
      render: (value, row) => (
        <div className="my-work-inbox__type">
          <span><strong>{ value }</strong>{ row.is_delegated ? <Tag color="orange">Delegated</Tag> : null }</span>
          { row.delegator ? <small>Delegated by { row.delegator.name }</small> : null }
        </div>
      ),
    },
    { title: 'Code', dataIndex: 'code', render: (value) => value || '—' },
    { title: 'Employee', render: (_, row) => row.requestor?.name || '—' },
    { title: 'Submitted', dataIndex: 'submitted_at', render: (value) => value ? new Date(value).toLocaleDateString() : '—' },
    { title: 'Status', dataIndex: 'status', render: (value) => <Tag>{ value }</Tag> },
    { title: 'Summary', dataIndex: 'summary', ellipsis: true },
    { title: '', width: 56, render: (_, row) => <Button aria-label="Open request" icon={ <EyeOutlined /> } onClick={ () => openDetail(row) } /> },
  ];

  return (
    <section className="my-work-inbox">
      <header>
        <div>
          <h2>{ approvals ? 'My Approvals' : 'My Requests' }</h2>
          <p>{ approvals ? 'Requests currently waiting for your decision.' : 'Track all your submitted requests in one place.' }</p>
        </div>
        <div className="my-work-inbox__actions">
          { approvals ? <Button icon={ <FilterOutlined /> } onClick={ () => setFilterOpen(true) }>Filters</Button> : null }
          <Button icon={ <ReloadOutlined /> } onClick={ load }>Refresh</Button>
        </div>
      </header>
      <div className="my-work-inbox__filters">
        <Input.Search allowClear placeholder="Search by request code" onSearch={ (search) => setFilters((value) => ({ ...value, page: 1, search })) } />
        <Select allowClear placeholder="All request types" options={ TYPE_OPTIONS } onChange={ (type) => setFilters((value) => ({ ...value, page: 1, type })) } />
        { approvals ? (
          <Select
            defaultValue="all"
            options={ [
              { value: 'all', label: 'All approvals' },
              { value: 'direct', label: 'Direct' },
              { value: 'delegated', label: 'Delegated' },
            ] }
            onChange={ (assignment) => setFilters((value) => ({ ...value, page: 1, assignment })) }
          />
        ) : (
          <Select allowClear placeholder="All statuses" options={ ['Pending', 'Approved', 'Completed', 'Rejected', 'Cancelled'].map((value) => ({ value })) } onChange={ (status) => setFilters((value) => ({ ...value, page: 1, status })) } />
        ) }
      </div>
      <Spin spinning={ loading }>
        <Table rowKey={ (row) => row.detail_key } columns={ columns } dataSource={ result.data || [] } pagination={ false } locale={ { emptyText: <Empty description={ approvals ? 'No approvals are waiting for you' : 'No requests found' } /> } } />
      </Spin>
      { Number(result.meta?.total || 0) > Number(result.meta?.per_page || 20) ? (
        <Pagination current={ filters.page } pageSize={ result.meta.per_page } total={ result.meta.total } showSizeChanger={ false } onChange={ (page) => setFilters((value) => ({ ...value, page })) } />
      ) : null }
      { approvals && filterOpen ? (
        <FilterComponent
          filters={ approvalFilterFields() }
          selected={ advancedFilters }
          api={ getMyApprovalsFilterReq }
          moduleName="myApprovals"
          modkey="myApprovals"
          onFilter={ (value) => {
            setAdvancedFilters(value);
            setFilters((current) => ({ ...current, page: 1 }));
          } }
          onCloseFilter={ setFilterOpen }
        />
      ) : null }
    </section>
  );
};

function title(value) {
  return String(value)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
    .replace(/ Request$/, '');
}

function approvalFilterFields() {
  return getRequestFilters().map((field) => (
    ['delegator', 'next_requestee'].includes(field.key)
      ? { ...field, data_key: 'supporting_data.employments', resource_type: 'employment' }
      : field
  ));
}

export default MyWorkInbox;
